// /packages/shared-ui/hooks/useApi.ts
/**
 * API呼び出しのためのReact Hooks
 * データ取得、ローディング状態、エラー状態を管理する
 */
import { useState, useCallback } from "react";
import {
  apiGet,
  apiPost,
  apiPostFormData,
  apiPostBlob,
  apiPatch,
  apiPutJson,
  apiDelete,
} from "../utils/api";
import type { WhoamiMe } from "@shared-domain/auth-service/contract";

/**
 * 非同期呼び出しの状態 (PRODUCT_DEV-583 R11)。
 *
 * - `idle`: まだ一度も execute していない（自動 fetch はしないため、マウント直後は必ずこれ）
 * - `loading`: 実行中
 * - `success`: 完了（結果は 0 件かもしれない）
 * - `error`: 失敗
 *
 * `isLoading` は「未実行」と「実行中」を区別できず、一覧画面が未実行の状態を
 * 「0 件」と誤って描画していた（空状態 → ローディング の順序逆転）。判定は status で行う。
 */
export type ApiStatus = "idle" | "loading" | "success" | "error";

/**
 * API呼び出し結果の型
 */
export interface ApiResponse<T> {
  data: T | null;
  isLoading: boolean;
  /** R11: 未実行(idle)を含む4状態。描画分岐にはこちらを使う。 */
  status: ApiStatus;
  error: Error | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  execute: (...args: any[]) => Promise<T | null>;
  reset: () => void;
}

/**
 * 共通の状態管理ロジック
 */
function useApiState<T>(initialData: T | null = null) {
  const [data, setData] = useState<T | null>(initialData);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<ApiStatus>("idle");
  const [error, setError] = useState<Error | null>(null);

  const reset = useCallback(() => {
    setData(initialData);
    setIsLoading(false);
    setStatus("idle");
    setError(null);
  }, []);

  const run = useCallback(
    async <R extends T | null>(fn: () => Promise<R>): Promise<R | null> => {
      setIsLoading(true);
      setStatus("loading");
      setError(null);
      try {
        const result = await fn();
        setData(result);
        setStatus("success");
        return result;
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error(String(err));
        setError(errorObj);
        setStatus("error");
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return { data, isLoading, status, error, reset, run };
}

/**
 * URL/Data引数を解決するヘルパー
 * POST/PATCH/DELETEフックで共通の引数パターンを処理
 */
function resolveUrlAndData<D>(
  urlOrData: string | D,
  dataOrOptions: D | RequestInit | undefined,
  options: RequestInit | undefined,
  initialUrl?: string,
): { url: string; data: D; opts: RequestInit | undefined } | null {
  if (typeof urlOrData === "string") {
    return {
      url: urlOrData,
      data: dataOrOptions as D,
      opts: options,
    };
  }
  if (!initialUrl) return null;
  return {
    url: initialUrl,
    data: urlOrData,
    opts: dataOrOptions as RequestInit | undefined,
  };
}

/**
 * GET APIリクエストのためのフック
 */
export const useApiGet = <T = unknown>(
  auth: WhoamiMe,
  initialUrl?: string,
): ApiResponse<T> => {
  const { data, isLoading, status, error, reset, run } = useApiState<T>();

  const execute = useCallback(
    async (
      url: string = initialUrl as string,
      options?: RequestInit,
    ): Promise<T | null> => {
      if (!url) return null;
      return run(async () => {
        const u = new URL(url);
        u.searchParams.set("tenantId", auth.currentTenantId || "");
        return apiGet<T>(u.toString(), options);
      });
    },
    [initialUrl],
  );

  return { data, isLoading, status, error, execute, reset };
};

/**
 * POST APIリクエストのためのフック（JSON）
 */
export const useApiPost = <T = unknown, D = unknown>(
  auth: WhoamiMe,
  initialUrl?: string,
): ApiResponse<T> => {
  const { data, isLoading, status, error, reset, run } = useApiState<T>();

  const execute = useCallback(
    async (
      urlOrData: string | D,
      dataOrOptions?: D | RequestInit,
      options?: RequestInit,
    ): Promise<T | null> => {
      const resolved = resolveUrlAndData(
        urlOrData,
        dataOrOptions,
        options,
        initialUrl,
      );
      if (!resolved) return null;
      return run(async () => {
        const newData = {
          ...resolved.data,
          tenantId: auth.currentTenantId || "",
        };
        return apiPost<T, D>(resolved.url, newData, resolved.opts);
      });
    },
    [initialUrl],
  );

  return { data, isLoading, status, error, execute, reset };
};

/**
 * PATCH APIリクエストのためのフック（JSON）
 */
export const useApiPatch = <T = unknown, D = unknown>(
  auth: WhoamiMe,
  initialUrl?: string,
): ApiResponse<T> => {
  const { data, isLoading, status, error, reset, run } = useApiState<T>();

  const execute = useCallback(
    async (
      urlOrData: string | D,
      dataOrOptions?: D | RequestInit,
      options?: RequestInit,
    ): Promise<T | null> => {
      const resolved = resolveUrlAndData(
        urlOrData,
        dataOrOptions,
        options,
        initialUrl,
      );
      if (!resolved) return null;
      return run(async () => {
        const newData = {
          ...resolved.data,
          tenantId: auth.currentTenantId || "",
        };
        return apiPatch<T, D>(resolved.url, newData, resolved.opts);
      });
    },
    [initialUrl],
  );

  return { data, isLoading, status, error, execute, reset };
};

/**
 * PUT APIリクエストのためのフック（JSON・認証あり）
 *
 * PRODUCT_DEV-622: API Gateway が PUT でしかルートを持たないエンドポイント向け。
 * 挙動は `useApiPatch` と同一で HTTP メソッドだけが異なる。
 */
export const useApiPutJson = <T = unknown, D = unknown>(
  auth: WhoamiMe,
  initialUrl?: string,
): ApiResponse<T> => {
  const { data, isLoading, status, error, reset, run } = useApiState<T>();

  const execute = useCallback(
    async (
      urlOrData: string | D,
      dataOrOptions?: D | RequestInit,
      options?: RequestInit,
    ): Promise<T | null> => {
      const resolved = resolveUrlAndData(
        urlOrData,
        dataOrOptions,
        options,
        initialUrl,
      );
      if (!resolved) return null;
      return run(async () => {
        const newData = {
          ...resolved.data,
          tenantId: auth.currentTenantId || "",
        };
        return apiPutJson<T, D>(resolved.url, newData, resolved.opts);
      });
    },
    [initialUrl],
  );

  return { data, isLoading, status, error, execute, reset };
};

/**
 * DELETE APIリクエストのためのフック（JSON）
 */
export const useApiDelete = <T = unknown, D = unknown>(
  auth: WhoamiMe,
  initialUrl?: string,
): ApiResponse<T> => {
  const { data, isLoading, status, error, reset, run } = useApiState<T>();

  const execute = useCallback(
    async (
      urlOrData: string | D,
      dataOrOptions?: D | RequestInit,
      options?: RequestInit,
    ): Promise<T | null> => {
      const resolved = resolveUrlAndData(
        urlOrData,
        dataOrOptions,
        options,
        initialUrl,
      );
      if (!resolved) return null;
      return run(async () => {
        const newData = {
          ...resolved.data,
          tenantId: auth.currentTenantId || "",
        };
        return apiDelete<T, D>(resolved.url, newData, resolved.opts);
      });
    },
    [initialUrl],
  );

  return { data, isLoading, status, error, execute, reset };
};

/**
 * POST APIリクエストのためのフック（FormData）
 */
export const useApiPostFormData = <T = unknown>(
  initialUrl?: string,
): ApiResponse<T> => {
  const { data, isLoading, status, error, reset, run } = useApiState<T>();

  const execute = useCallback(
    async (
      urlOrFormData: string | FormData,
      formDataOrOptions?: FormData | RequestInit,
      options?: RequestInit,
    ): Promise<T | null> => {
      let url: string;
      let formData: FormData;
      let opts: RequestInit | undefined;

      if (typeof urlOrFormData === "string") {
        url = urlOrFormData;
        formData = formDataOrOptions as FormData;
        opts = options;
      } else {
        if (!initialUrl) return null;
        url = initialUrl;
        formData = urlOrFormData;
        opts = formDataOrOptions as RequestInit | undefined;
      }

      return run(async () => apiPostFormData<T>(url, formData, opts));
    },
    [initialUrl],
  );

  return { data, isLoading, status, error, execute, reset };
};

/**
 * POST APIリクエストのためのフック（Blob応答）
 */
export interface BlobApiResponse<D = unknown> {
  data: { blob: Blob | null; headers: Headers | null };
  isLoading: boolean;
  error: Error | null;
  execute: (data: D) => Promise<{ blob: Blob; headers: Headers } | null>;
  reset: () => void;
}

export const useApiPostBlob = <D = unknown>(
  initialUrl?: string,
): BlobApiResponse<D> => {
  const { data, isLoading, error, reset, run } = useApiState<{
    blob: Blob | null;
    headers: Headers | null;
  }>({ blob: null, headers: null });

  const execute = useCallback(
    async (
      urlOrData: string | D,
      dataOrOptions?: D | RequestInit,
      options?: RequestInit,
    ): Promise<{ blob: Blob; headers: Headers } | null> => {
      let url: string;
      let reqData: D;
      let opts: RequestInit | undefined;

      if (typeof urlOrData === "string") {
        url = urlOrData;
        reqData = dataOrOptions as D;
        opts = options;
      } else {
        if (!initialUrl) return null;
        url = initialUrl;
        reqData = urlOrData;
        opts = dataOrOptions as RequestInit | undefined;
      }

      return run(async () => apiPostBlob<D>(url, reqData, opts));
    },
    [initialUrl],
  );

  return {
    data: data ?? { blob: null, headers: null },
    isLoading,
    error,
    execute,
    reset,
  };
};
